import './promptModal.scss'
import templateUrl from './promptModal.html'
import templateUrlFullScreen from './promptModal.fullScreen.html'

/**
 * Configures a prompt Modal with Ok/Cancel options using $mdPanel
 *
 * @type {ngConfig}
 */
function tmrPromptModalConfig($mdPanelProvider) {
  'ngInject'
  $mdPanelProvider.definePreset('promptModal', {
    controller: 'tmrPromptModalController',
    controllerAs: '$ctrl',
    panelClass: 'tmr-modal tmr-prompt-modal md-whiteframe-1dp',
    escapeToClose: true,
  })
}

/**
 * Simplifies the usage of the {@link TmrPromptModal promptModal panel}
 *
 * @example <caption>In a controller</caption>
 * this.TmrPromptModal($event, {
 *   question: 'SOME.TRANSLATION.KEY'},
 *   yesCallback: MdPanelRef => { // Do something... }
 * });
 *
 * @alias TmrPromptModal
 * @type {ngFactory}
 *
 * @prop {Event} openFrom The click event passed by the template
 * @prop {boolean=} [options.fullScreen=false] If true, the modal is rendered centered to the screen with a backdrop; if false, the modal is rendered as a tooltip next to the `relativeTo` element
 * @prop {string} options.question The translation key for the question presented to the user
 * @prop {object=} options.questionVariables Optional translation variables for the question, passed to translation-values directive
 * @prop {function} options.yesCallback A callback that is executed only if the user answers yes
 * @prop {string=} options.relativeTo A selector of the element that governs the panel position
 * @prop {string=} options.icon An optional icon class
 */
function TmrPromptModalFactory($mdPanel, $document) {
  'ngInject'

  return (
    openFrom,
    {
      fullScreen = false,
      question,
      questionVariables,
      yesCallback,
      relativeTo = openFrom.target,
      icon,
    }
  ) => {
    let position

    if (fullScreen) {
      position = $mdPanel
        .newPanelPosition()
        .relativeTo($document[0].body)
        .addPanelPosition($mdPanel.xPosition.CENTER, $mdPanel.yPosition.CENTER)
    } else {
      position = $mdPanel
        .newPanelPosition()
        .relativeTo(relativeTo)
        .addPanelPosition(
          $mdPanel.xPosition.OFFSET_START,
          $mdPanel.yPosition.CENTER
        )
    }

    let animation = $mdPanel
      .newPanelAnimation()
      .openFrom(openFrom)
      .withAnimation($mdPanel.animation.FADE)

    return $mdPanel.open('promptModal', {
      templateUrl: fullScreen ? templateUrlFullScreen : templateUrl,
      hasBackdrop: fullScreen,
      clickOutsideToClose: !fullScreen,
      openFrom,
      position,
      animation,
      onCloseSuccess: (MdPanelRef, reason) => {
        if (reason == 'PROMPTMODAL_YES') yesCallback(MdPanelRef)
      },
      locals: { promptModalLocals: { question, questionVariables, icon } },
    })
  }
}

/**
 * Controller for the panel preset promptModal
 *
 * @type {ngController}
 * @see TmrPromptModal
 */
export class tmrPromptModalController {
  static ngType = 'controller'

  constructor(mdPanelRef, promptModalLocals) {
    'ngInject'
    Object.assign(this, { mdPanelRef, promptModalLocals })
  }

  $onInit() {
    this.yesLabel = this.yesLabel ?? 'PROMPTMODAL.YES'
    this.noLabel = this.noLabel ?? 'PROMPTMODAL.NO'
  }

  yes() {
    this.mdPanelRef.close('PROMPTMODAL_YES')
  }

  no() {
    this.mdPanelRef.close('PROMPTMODAL_NO')
  }
}

export { tmrPromptModalConfig as config }
export const factory = ['TmrPromptModal', TmrPromptModalFactory]
